// TODO: Review AI Code for feature: System Landscape & Interactions Visualization - Entire file is AI-generated
import { useTranslation } from 'react-i18next';
import { Maximize2, Minimize2, RotateCcw, Pause, Play, type LucideIcon } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  Skeleton,
  cn
} from '@bliks-io/ui';

// ============================================================================
// View Toggle
// ============================================================================

export interface ViewModeOption<T extends string = string> {
  value: T;
  icon: LucideIcon;
  /** CSS class condition to hide on mobile */
  hideOnMobile?: boolean;
  /** CSS class condition to hide on tablet */
  hideOnTablet?: boolean;
}

interface ViewToggleProps<T extends string = string> {
  modes: ViewModeOption<T>[];
  active: T;
  onChange: (mode: T) => void;
  /** Function to get the label for a mode (from i18n) */
  getLabel: (mode: T) => string;
}

export function ViewToggle<T extends string>({
  modes,
  active,
  onChange,
  getLabel
}: ViewToggleProps<T>) {
  return (
    <div className="flex items-center gap-0.5 rounded-md border bg-muted/50 p-0.5">
      {modes.map(({ value, icon: Icon, hideOnMobile, hideOnTablet }) => (
        <button
          key={value}
          type="button"
          onClick={() => onChange(value)}
          className={cn(
            'inline-flex items-center gap-1 rounded-sm px-2 py-1 text-xs font-medium transition-colors',
            hideOnTablet && 'hidden lg:inline-flex',
            !hideOnTablet && hideOnMobile && 'hidden sm:inline-flex',
            active === value
              ? 'bg-background text-foreground shadow-xs'
              : 'text-muted-foreground hover:text-foreground'
          )}
        >
          <Icon className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">{getLabel(value)}</span>
        </button>
      ))}
    </div>
  );
}

// ============================================================================
// Visualization Toolbar (pause/play, reset, fullscreen)
// ============================================================================

const toolbarBtnClass =
  'inline-flex items-center rounded-sm p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors';

interface VisualizationToolbarProps {
  /** Show rotation controls only when the 3D view is active */
  show3DControls: boolean;
  isFullscreen: boolean;
  isRotating: boolean;
  onToggleRotation: () => void;
  onReset: () => void;
  onToggleFullscreen: () => void;
}

export function VisualizationToolbar({
  show3DControls,
  isFullscreen,
  isRotating,
  onToggleRotation,
  onReset,
  onToggleFullscreen
}: VisualizationToolbarProps) {
  const { t } = useTranslation();
  return (
    <TooltipProvider delayDuration={0}>
      <div className="flex items-center gap-0.5">
        {show3DControls && (
          <Tooltip>
            <TooltipTrigger asChild>
              <button type="button" onClick={onToggleRotation} className={toolbarBtnClass}>
                {isRotating ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
              </button>
            </TooltipTrigger>
            <TooltipContent>
              {isRotating
                ? t('organization.systems.interactions.pause')
                : t('organization.systems.interactions.play')}
            </TooltipContent>
          </Tooltip>
        )}
        <Tooltip>
          <TooltipTrigger asChild>
            <button type="button" onClick={onReset} className={toolbarBtnClass}>
              <RotateCcw className="w-3.5 h-3.5" />
            </button>
          </TooltipTrigger>
          <TooltipContent>{t('organization.systems.interactions.resetView')}</TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <button type="button" onClick={onToggleFullscreen} className={toolbarBtnClass}>
              {isFullscreen ? (
                <Minimize2 className="w-3.5 h-3.5" />
              ) : (
                <Maximize2 className="w-3.5 h-3.5" />
              )}
            </button>
          </TooltipTrigger>
          <TooltipContent>
            {isFullscreen
              ? t('organization.systems.interactions.exitFullscreen')
              : t('organization.systems.interactions.fullscreen')}
          </TooltipContent>
        </Tooltip>
      </div>
    </TooltipProvider>
  );
}

// ============================================================================
// Visualization Fallback (shared loading state)
// ============================================================================

export function VisualizationFallback({ height = 300 }: { height?: number } = {}) {
  const { t } = useTranslation();
  return (
    <div
      role="status"
      aria-label={t('common.loading')}
      className="flex items-center justify-center"
      style={{ minHeight: height }}
    >
      <Skeleton className="w-full rounded-md" style={{ height }} />
    </div>
  );
}
